import i18next from 'i18next';
import { FC, useCallback, useEffect, useState } from 'react';
import { useMicrophoneStatus } from '@/hooks/useMicrophoneStatus';
import { call } from '@/services/call';
import { logger } from '@/services/logger';
import { resetRoom } from '@/store/features/room/slice';
import { showUIAlert } from '@/store/features/ui/slice';
import { AlertActions, AlertTypes } from '@/store/features/ui/types';
import { resetUser } from '@/store/features/user/slice';
import { useAppDispatch } from '@/store/hooks';
import { Splash } from '@/ui';
import { twilioTokenStorage } from '@/utils/storage';

type Props = {
  children: React.ReactNode;
}

const ConnectRoom: FC<Props> = ({ children }) => {
  useMicrophoneStatus();

  const dispatch = useAppDispatch();

  const [isConnecting, setIsConnecting] = useState(true);
  const [error, setError] = useState(false);

  const handleError = useCallback((error: unknown) => {
    logger.error(error);
    dispatch(
      showUIAlert({
        title: i18next.t('error'),
        message: i18next.t('something_went_wrong'),
        type: AlertTypes.error,
        action: AlertActions.navigateHome,
      })
    );
    setError(true);
  }, [dispatch]);

  useEffect(() => {
    const joinRoom = async () => {
      try {
        const roomId = window.location.pathname.split('/room/')[1];

        if (!roomId) {
          throw new Error('Missing room ID');
        }

        const twilioToken = twilioTokenStorage.getItem();
        if (!twilioToken) {
          window.location.href = `/home/${roomId}`;
          return;
        }

        await call.connect(twilioToken, roomId, handleError);
      } catch (error) {
        handleError(error);
      } finally {
        setIsConnecting(false);
      }
    };

    joinRoom();

    return () => {
      call.destroy();
      dispatch(resetRoom());
      dispatch(resetUser());
    };
  }, [dispatch, handleError]);

  if (isConnecting) {
    return <Splash />;
  }

  if (error) {
    return null;
  }

  return <>{children}</>;
};

export default ConnectRoom;